import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";

import EyeInput from '../../../assets/eye-input.png';
import EyeSlashInput from '../../../assets/eye-slash-input.png';
import IconInputClose from '../../../assets/icons/icon-close-gray.png';
import IconSearchBlack from '../../../assets/icons/icon-search-black.svg';
import InformationErro from '../../../assets/icons/icon-information-red.svg';

import * as S from "./styles_v3";
import { BodyP } from "../../../styles/stylesTypography";
import { theme } from "../../../styles/theme";
import Interruptor from "../../interruptor";

const InputV3 = forwardRef(({
  id,
  label,
  floatingLabel,
  floatingLabelWithAsterisk,
  extraInfo,
  placeholder,
  hasError,
  errorInfo,
  isDisabled,
  type,
  value,
  onChange,
  onBlur,
  options,
  typeValueSelect,
  radius,
  searchLeft,
  floatingValue,
  handleClear,
  maxLength,
  yesNo,
  initialYesNoValue,
  messageYesNo,
  onChangeYesNo,
  hideYesNoMobile,
  hideYesNo,
  marginBottom,
  minHeight,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const [hasValue, setHasValue] = useState(false);
  const [hasFloatingValue, setHasFloatingValue] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const [showPassword, setShowPassword] = useState(false);
  const [yesNoValue, setYesNoValue] = useState(false);
  const inputRef = useRef(null);

  const [selectedValue, setSelectedValue] = useState("");

  useEffect(() => {
    setInputValue(value || "");
    setHasValue(!!value);
    setHasFloatingValue(!!floatingValue);
  }, [value, floatingValue]);

  const handleFocus = () => {
    if (!isDisabled) setIsFocused(true);
  };

  const handleBlur = (e) => {
    if (!e.relatedTarget || !e.relatedTarget.closest('button')) {
      setIsFocused(false);
    }
    setHasValue(!!e.target.value);

    if (onBlur) {
      onBlur(e);
    }
  };

  const filterNomeInput = (value) => {
    return value.replace(/[^A-Za-zÀ-ú\s]/g, '');
  };

  const filterTelefoneInput = (value) => {
    return value.replace(/[^0-9]/g, ''); 
  };

  const formatTelefone = (value) => {
    const numbers = filterTelefoneInput(value); 
    let formattedValue = numbers;

    if (numbers.length > 2) {
      formattedValue = `(${numbers.slice(0, 2)})${numbers.slice(2)}`;
    }
    if (numbers.length > 6 && numbers.length <= 10) {
      formattedValue = `${formattedValue.slice(0, 8)}-${formattedValue.slice(8, 12)}`;
    } else if (numbers.length > 10) {
      formattedValue = `${formattedValue.slice(0, 9)}-${formattedValue.slice(9, 13)}`;
    }

    return formattedValue;
  };

  const handleChange = (e) => {
    const selectedValue = e.target.value;

    if (type === "select") {
      const selectedOption = options.find(
        (option) => String(option.value) === selectedValue
      );
  
      const parsedValue = selectedOption
      ? {
          value: typeValueSelect === "number" ? Number(selectedOption.value) : String(selectedOption.value),
          label: selectedOption.label,
        }
      : { value: "", label: "" };
  
      setInputValue(floatingValue ? floatingValue : parsedValue.label ? parsedValue.label : "");

      setSelectedValue(floatingValue ? "" : selectedValue);
  
      if (onChange) {
        onChange(parsedValue);
      }
  
      if (inputRef.current) {
        inputRef.current.blur();
      }
    } else if (type === "nome") {
      const newValue = filterNomeInput(selectedValue);
      setInputValue(newValue);
      if (onChange) {
        onChange({ ...e, target: { ...e.target, value: newValue } });
      }
    } else if (type === "telefone/celular") {
      const newValue = formatTelefone(selectedValue);
      setInputValue(newValue);
      if (onChange) {
        onChange({ ...e, target: { ...e.target, value: newValue } });
      }
    } else {
      setInputValue(selectedValue);
      if (onChange) {
        onChange({ ...e, target: { ...e.target, value: selectedValue } });
      }
    }

    setHasValue(!!selectedValue);
  };


  const handleClearInput = (e) => {
    e.preventDefault();
    
    if (type === "select") {
        setSelectedValue("");
        setInputValue("");

        if (onChange) {
            onChange({ value: null, label: "" });
        }
    } else {
        setInputValue("");

        if (onChange) {
            onChange({ target: { value: "" } });
        }
    }
    
    setHasValue(false);

    if (!isDisabled) {
      inputRef.current.focus();
    }
  };

  const onToggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const getInputType = () => {
    if (type === "password") {
      return showPassword ? "text" : "password";
    }
    return type === "number" ? "number" : type ? type : "text";
  };

  const inputType = getInputType();

  const formatDateTime = (value) => {
  if (!value) return "";

  const [datePart, timePart] = value.split("T");
  const [year, month, day] = datePart.split("-");
  const [hour, minute] = timePart.split(":");

  return `${day}/${month}/${year} - ${hour}:${minute}`;
};


  const clearSelect = () => {
    if (type === "select") {
      setSelectedValue("");
      setInputValue("");
      
      if (onChange) {
        onChange({ value: null, label: "" });
      }
      
      setHasValue(false);
    }
  };
  
  useImperativeHandle(ref, () => ({
    clearSelect,
    focus: () => inputRef.current?.focus()
  }));

  const keyDown = (e) => {
    if (props.onKeyDown) {
        props.onKeyDown(e);
    }
  }

  useEffect(() => {
    if (onChangeYesNo) {
      onChangeYesNo(yesNoValue);
    }
  }, [yesNoValue]);

  useEffect(() => {
    if (initialYesNoValue) {
      setYesNoValue(initialYesNoValue);
    }
  }, [initialYesNoValue]);

  return (
    <S.Container marginBottom={marginBottom} minHeight={minHeight}>
      <S.ContainerInputs>
      {type === "select" ? (
          <>
            <S.ContainerSelect>
              <S.Select
                id={id}
                label={label}
                value={selectedValue} 
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                hasError={hasError}
                disabled={isDisabled}
                ref={inputRef}
                radius={radius}
                {...props}
              >
                <option value="" disabled hidden></option>

                {options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </S.Select>

              <S.ArrowIcon />
            </S.ContainerSelect>

            {floatingValue && (
              <div style={
                {position: 'absolute', 
                width: 'auto', 
                height: 'auto',
                paddingRight: "2.2rem",
                top: '15px', 
                left: '17px', 
                background: 'white'
                }}>
                <BodyP color={theme.colors.gray.Cinza_2}>{floatingValue}</BodyP>
              </div>
            )}

            {floatingLabel && (
              <S.Label
                hasError={hasError}
                isDisabled={isDisabled}
                isFocusedOrHasValue={isFocused || hasValue || hasFloatingValue}
              >  
                <p>{floatingLabel}</p>
              </S.Label>
            )}

            {floatingLabelWithAsterisk && (
              <S.LabelWithAsterisk
                hasError={hasError}
                isDisabled={isDisabled}
                isFocusedOrHasValue={isFocused || hasValue || hasFloatingValue}
              >
                <span>*</span>
                
                <p>{floatingLabelWithAsterisk}</p>
              </S.LabelWithAsterisk>
            )}

            {isFocused && !hasValue && (
              <S.Placeholder hasError={hasError} isFocused={isFocused}>
                <span>{placeholder}</span>
              </S.Placeholder>
            )}
          </>
        ) : type === "datetime-local" ? (
          <>
          <S.Input
            id={id}
            type="datetime-local"
            value={inputValue}
            onChange={(e) => {
              const rawValue = e.target.value; // Valor no formato YYYY-MM-DDTHH:mm

              if (!rawValue) {
                setInputValue("");
          
                if (onChange) {
                  onChange({ target: { value: "" } });
                }
                return;
              }
          
              const formatted = formatDateTime(rawValue); 
              setInputValue(rawValue); 
          
              if (onChange) {
                onChange({ target: { value: rawValue } }); 
              }
            }}
            onFocus={(e) => {
              handleFocus();
              if (e.target.showPicker) {
                e.target.showPicker();
              }
            }}
            onBlur={(e) => {
              handleBlur(e);
             
            }}
            onKeyDown={(e) => keyDown(e.key)}
            hasError={hasError}
            disabled={isDisabled}
            radius={radius}
            {...props}
          />

          <S.CalendarIconWrapper />

          {floatingLabel && floatingLabel !== "" && (
            <S.Label
              hasError={hasError}
              isDisabled={isDisabled}
              isFocusedOrHasValue={isFocused || hasValue}
              searchLeft={searchLeft}
            >
              <p>{floatingLabel}</p>
            </S.Label>
          )}
      
          {floatingLabelWithAsterisk && floatingLabelWithAsterisk !== "" && (
            <S.LabelWithAsterisk
              hasError={hasError}
              isDisabled={isDisabled}
              isFocusedOrHasValue={isFocused || hasValue}
            >
              <span>*</span>
              <p>{floatingLabelWithAsterisk}</p>
            </S.LabelWithAsterisk>
          )}
        </>
        ) : (
          <>
            <S.Input
              id={id}
              type={inputType}
              label={label}
              placeholder=""
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleChange}
              onKeyDown={(e) => keyDown(e.key)}
              hasError={hasError}
              value={inputValue}
              ref={inputRef}
              disabled={isDisabled}
              radius={radius}
              searchLeft={searchLeft}
              maxLength={maxLength}
              {...props}
            />

            {floatingLabel && (
              <S.Label
                hasError={hasError}
                isDisabled={isDisabled}
                isFocusedOrHasValue={isFocused || hasValue}
                searchLeft={searchLeft}
              >
                <p>{floatingLabel}</p>
              </S.Label>
            )}

            {floatingLabelWithAsterisk && (
              <S.LabelWithAsterisk
                hasError={hasError}
                isDisabled={isDisabled}
                isFocusedOrHasValue={isFocused || hasValue}
              >
                <span>*</span>

                <p>{floatingLabelWithAsterisk}</p>
              </S.LabelWithAsterisk>
            )}

            {isFocused && !hasValue && (
              <S.Placeholder hasError={hasError} isFocused={isFocused} searchLeft={searchLeft}>
                <span>{placeholder}</span>
              </S.Placeholder>
            )}

            {isFocused && hasValue && !isDisabled && (
              <S.ClearButton
                type={type}
                hasError={hasError}
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleClear ? handleClear : handleClearInput}
                extraInfo={extraInfo}
              >
                <span>
                  <img src={IconInputClose} alt="Ícone de fechar cinza" />
                </span>
              </S.ClearButton>
            )}

            {hasError && errorInfo ? (
              <S.DescriptionInput hasError={hasError}>
                <img src={InformationErro} alt="Informação inválida" />

                <p>{errorInfo}</p>
              </S.DescriptionInput>
            ) : (
              extraInfo && (
                <S.DescriptionInput isDisabled={isDisabled}>
                  <p>{extraInfo}</p>
                </S.DescriptionInput>
              )
            )}

            {type === "password" && (
              <S.TogglePasswordButton
                onClick={onToggleShowPassword}
                type="button"
                showPassword={showPassword}
                hasError={hasError}
              >
                <img
                  src={showPassword ? EyeInput : EyeSlashInput}
                  alt={showPassword ? "Ocultar senha" : "Mostrar senha"}
                />
              </S.TogglePasswordButton>
            )}

            {type === "search" && (
              <S.SearchButton type="button" searchLeft={searchLeft}>
                <img src={IconSearchBlack} alt="Ícone de pesquisa" />
              </S.SearchButton>
            )}

            {yesNo === true && (
              <S.YesNoContainer hideMobile={hideYesNoMobile} hidden={hideYesNo} disabled={isDisabled}>
                <span>{messageYesNo}</span>
                <Interruptor value={yesNoValue} onChange={(e) => setYesNoValue(e)} disabled={isDisabled} />
              </S.YesNoContainer>
            )}
          </>
        )}
      </S.ContainerInputs>
    </S.Container>
  );
});

export default InputV3;